import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
    ShieldCheck,
    Briefcase,
    Package,
    Stethoscope,
    Flame,
    Network,
    ArrowRight,
    Check,
    Users,
    Link2,
    FileSignature,
    Handshake,
} from "lucide-react";
import { FadeUp, Overline, SectionHeading } from "@/components/visual/SectionHeading";
import GlobeLines from "@/components/visual/GlobeLines";
import SEO from "@/components/seo/SEO";

const services = [
    {
        id: "strategic-advisory",
        icon: Briefcase,
        title: "Strategic Advisory & Consultancy",
        lead: "Business and transaction strategy across sectors and jurisdictions.",
        points: [
            "Business and transaction strategy",
            "Market entry advisory",
            "Cross-sector strategic planning",
            "Institutional and government engagement",
        ],
    },
    {
        id: "defence-security",
        icon: ShieldCheck,
        title: "Defence & Security Advisory",
        lead: "Procurement advisory and market access within regulated frameworks.",
        points: [
            "Procurement advisory and structuring",
            "OEM representation and market access",
            "Compliance alignment and documentation",
            "Strategic partnerships within defence ecosystems",
        ],
    },
    {
        id: "international-trade",
        icon: Package,
        title: "International Trade & Commercial Advisory",
        lead: "Structured advisory and facilitation across import-export, commodities, supply chain, and cross-border commerce.",
        points: [
            "Import-export advisory and structuring",
            "Commodities trading support (non-principal advisory role)",
            "Supplier and buyer coordination",
            "Supply chain and logistics advisory",
            "Commercial contract structuring",
            "Cross-border transaction facilitation",
        ],
    },
    {
        id: "pharma-healthcare",
        icon: Stethoscope,
        title: "Pharmaceuticals & Healthcare Advisory",
        lead: "Public health, infrastructure, and institutional healthcare advisory.",
        points: [
            "Public health and healthcare infrastructure advisory",
            "Medical supply chain structuring",
            "Institutional healthcare projects",
            "Policy alignment and program advisory",
        ],
    },
    {
        id: "energy",
        icon: Flame,
        title: "Oil & Gas & Energy Advisory",
        lead: "Energy transaction structuring, supply chains, and market access.",
        points: [
            "Energy transaction structuring",
            "Crude and refined product advisory",
            "Infrastructure and supply chain frameworks",
            "Market access and partnership development",
        ],
    },
    {
        id: "cross-border",
        icon: Network,
        title: "Cross-Border Transaction Structuring",
        lead: "Multi-jurisdiction deal structuring and financial instrument advisory.",
        points: [
            "Multi-jurisdiction deal structuring",
            "Financial instrument advisory (SBLC, LC, BG)",
            "Stakeholder alignment",
            "Execution support and coordination",
        ],
    },
];

const executionCapabilities = [
    { icon: Users, title: "Representation & Market Access", desc: "On-ground representation and market entry across target jurisdictions." },
    { icon: Link2, title: "Deal Structuring & Execution Support", desc: "Commercial architecture and transaction support across parties and regulators." },
    { icon: FileSignature, title: "Documentation & Negotiation Advisory", desc: "Advisory through term sheets, agreements, and cross-party negotiation." },
    { icon: Handshake, title: "Strategic Partnerships & Introductions", desc: "Institutional introductions and long-term partnership development." },
];

const Services = () => {
    return (
        <div className="page-enter">
            <SEO
                title="Services — Strategic, Defence, Trade, Energy & Healthcare Advisory"
                description="Six advisory practices including Strategic Advisory, Defence & Security, International Trade & Commodities, Pharmaceuticals & Healthcare, Oil & Gas & Energy, and Cross-Border Transaction Structuring."
                path="/services"
                keywords="strategic advisory services, defence procurement advisory, trade finance advisory, SBLC LC BG, commodities advisory, healthcare advisory, oil and gas advisory, cross-border transaction structuring"
            />
            {/* HERO */}
            <section className="relative min-h-[55vh] flex items-center overflow-hidden" data-testid="services-hero">
                <GlobeLines className="opacity-10" />
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-28 relative z-10 w-full">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <Overline>Services</Overline>
                        <h1 className="font-serif text-5xl md:text-7xl text-white leading-[1.02] mt-6 max-w-4xl">
                            Six practices. <span className="italic font-light text-[#C9A14A]">One advisory standard.</span>
                        </h1>
                        <p className="mt-8 text-slate-400 max-w-2xl text-lg font-light leading-relaxed">
                            Structured advisory and facilitation across sectors. Each practice is grounded in
                            institutional experience and multi-jurisdictional coordination.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* SERVICES LIST */}
            <section className="py-16 md:py-24" data-testid="services-list">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="border-t border-[#C9A14A]/20">
                        {services.map((s, i) => {
                            const Icon = s.icon;
                            return (
                                <FadeUp key={s.id} delay={i * 0.04}>
                                    <div
                                        id={s.id}
                                        className="grid grid-cols-1 lg:grid-cols-12 gap-10 py-16 md:py-20 border-b border-[#C9A14A]/20 scroll-mt-28"
                                        data-testid={`service-row-${s.id}`}
                                    >
                                        <div className="lg:col-span-1">
                                            <div className="font-serif text-4xl text-[#C9A14A] italic">{String(i + 1).padStart(2, "0")}</div>
                                        </div>
                                        <div className="lg:col-span-5">
                                            <Icon size={36} strokeWidth={1.1} className="text-[#C9A14A] mb-6" />
                                            <h2 className="font-serif text-3xl md:text-4xl text-white leading-[1.08]">{s.title}</h2>
                                            <p className="mt-6 text-slate-400 font-light leading-relaxed max-w-md">{s.lead}</p>
                                        </div>
                                        <div className="lg:col-span-6">
                                            <ul className="space-y-5">
                                                {s.points.map((p) => (
                                                    <li key={p} className="flex items-start gap-4 pb-5 border-b border-[#C9A14A]/10 last:border-0">
                                                        <Check size={16} className="text-[#C9A14A] mt-1 shrink-0" strokeWidth={2} />
                                                        <span className="text-slate-300 font-light">{p}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                            <Link to="/contact" className="mt-8 inline-flex items-center gap-2 text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] hover:gap-4 transition-all">
                                                Initiate Engagement <ArrowRight size={12} />
                                            </Link>
                                        </div>
                                    </div>
                                </FadeUp>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* INTERNATIONAL TRADE — CAPABILITIES + TRANSACTION TYPES */}
            <section className="py-24 md:py-32 border-t border-[#C9A14A]/15" data-testid="services-trade-detail">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <SectionHeading
                        overline="International Trade & Commercial Facilitation"
                        title="Enabling efficient global trade through structured advisory."
                        description="Indravax supports international trade and cross-border commerce through structured advisory, supply chain coordination, and transaction facilitation."
                        testId="services-trade-heading"
                    />
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                        <FadeUp>
                            <div className="institutional-card h-full" data-testid="trade-capabilities">
                                <Overline>Capabilities</Overline>
                                <h3 className="font-serif text-3xl text-white mt-4 leading-tight">Trade structuring & coordination</h3>
                                <ul className="mt-8 space-y-5">
                                    {[
                                        "Multi-jurisdiction trade alignment",
                                        "Incoterms structuring (FOB, CIF, DAP, etc.)",
                                        "Documentation advisory (LOI, FCO, SPA)",
                                        "Trade finance coordination (LC, SBLC, BG)",
                                        "Risk mitigation and execution support",
                                    ].map((c) => (
                                        <li key={c} className="flex items-start gap-4 pb-5 border-b border-[#C9A14A]/10 last:border-0">
                                            <Check size={16} className="text-[#C9A14A] mt-1 shrink-0" strokeWidth={2} />
                                            <span className="text-slate-300 font-light">{c}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </FadeUp>
                        <FadeUp delay={0.1}>
                            <div className="institutional-card h-full" data-testid="trade-transaction-types">
                                <Overline>Transactions Supported</Overline>
                                <h3 className="font-serif text-3xl text-white mt-4 leading-tight">International trade transactions</h3>
                                <ul className="mt-8 space-y-5">
                                    {[
                                        "Commodity supply agreements",
                                        "Import-export contracts",
                                        "Cross-border distribution arrangements",
                                        "Supply chain structuring engagements",
                                        "Trade finance-backed transactions",
                                    ].map((c, i) => (
                                        <li key={c} className="flex items-start gap-4 pb-5 border-b border-[#C9A14A]/10 last:border-0">
                                            <span className="text-[0.65rem] tracking-[0.3em] text-[#C9A14A] mt-1 shrink-0 w-6">0{i + 1}</span>
                                            <span className="text-slate-300 font-light">{c}</span>
                                        </li>
                                    ))}
                                </ul>
                                <div className="mt-8 inline-flex items-center gap-3">
                                    <span className="h-px w-8 bg-[#C9A14A]" />
                                    <span className="overline">Advisory · Facilitation · Coordination</span>
                                </div>
                            </div>
                        </FadeUp>
                    </div>
                    <FadeUp delay={0.2} className="mt-16 max-w-3xl mx-auto text-center">
                        <p className="font-serif text-xl md:text-2xl text-slate-300 italic leading-relaxed">
                            Structured advisory and facilitation across global trade, supply chains, and
                            cross-border commercial transactions.
                        </p>
                        <p className="mt-6 text-xs text-slate-500 font-light leading-relaxed border-l-2 border-[#C9A14A]/40 pl-5 italic max-w-2xl mx-auto text-left">
                            Indravax supports international trade transactions through structured facilitation,
                            coordination between counterparties, and alignment of commercial, logistical, and
                            financial frameworks. The firm does not act as a principal buyer or seller unless
                            specifically authorized under applicable laws and agreements.
                        </p>
                    </FadeUp>
                </div>
            </section>

            {/* EXECUTION & FACILITATION */}
            <section className="py-24 md:py-32 bg-[#07132E]/40 relative overflow-hidden" data-testid="services-execution">
                <GlobeLines className="opacity-[0.06]" />
                <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
                    <SectionHeading
                        overline="Execution & Facilitation"
                        title="Advisory translated into transaction support."
                        description="Indravax supports transactions through representation, structuring, documentation, and partnership development — the mechanics that turn advisory into engagements."
                        testId="services-execution-heading"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {executionCapabilities.map((e, i) => {
                            const Icon = e.icon;
                            return (
                                <FadeUp key={e.title} delay={i * 0.06}>
                                    <div className="institutional-card h-full" data-testid={`exec-cap-${i}`}>
                                        <Icon size={28} strokeWidth={1.1} className="text-[#C9A14A]" />
                                        <h3 className="font-serif text-xl text-white mt-6 leading-tight">{e.title}</h3>
                                        <p className="text-sm text-slate-400 mt-3 font-light leading-relaxed">{e.desc}</p>
                                    </div>
                                </FadeUp>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* REGULATORY DISCLAIMER */}
            <section className="py-20 border-t border-[#C9A14A]/15" data-testid="services-disclaimer">
                <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
                    <Overline>Regulatory Disclaimer</Overline>
                    <p className="mt-6 text-slate-400 font-light leading-[1.9] max-w-3xl mx-auto">
                        Indravax Strategic Advisory LLP provides advisory and facilitation services. The firm
                        does not undertake any activity requiring regulatory approval without obtaining the
                        necessary licenses, permissions, or authorizations as required under applicable laws.
                    </p>
                </div>
            </section>

            {/* CTA */}
            <section className="py-20 border-t border-[#C9A14A]/15 bg-[#07132E]/40" data-testid="services-cta">
                <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
                    <SectionHeading
                        align="center"
                        overline="Engage"
                        title="Bring your mandate to an advisory firm with sector depth."
                        description="Every engagement begins with a confidential assessment. We respond within two working days."
                        testId="services-cta-heading"
                    />
                    <Link to="/contact" className="btn-primary" data-testid="services-cta-btn">
                        Request Consultation <ArrowRight size={14} />
                    </Link>
                </div>
            </section>
        </div>
    );
};

export default Services;
